<script setup lang="ts">
  import type { SubscribeBlockPopupStoryblok } from '@/storyblok/component-types-sb'
  import { marked } from 'marked'
  import { useSubscribeDataLayer } from '@/composables/data-layers/useSubscribeDataLayer'
  import { useNewsletterSubscription } from '@/composables/useNewsletterSubscription'

  const props = defineProps<{ blok: SubscribeBlockPopupStoryblok }>()

  const { isDesktop } = useBTBreakpoints()
  const dayjs = useDayjs()

  const SIX_MONTHS_MS = 15552000
  const hasSeenSubscribeBlockPopup = useCookie<boolean>('airbaltic_seen_subscribe_to_popup', {
    secure: true,
    priority: 'high',
    expires: dayjs().add(SIX_MONTHS_MS, 's').toDate(),
    maxAge: SIX_MONTHS_MS
  })

  const { pushSubscribeBlockDataLayer } = useSubscribeDataLayer()
  const onSuccessfulSubmitDataLayer = () => {
    pushSubscribeBlockDataLayer({
      event_category: 'submitted'
    })
  }
  const onErrorSubmitDataLayer = (errorMessage: string) => {
    pushSubscribeBlockDataLayer({
      event_category: 'error',
      event_message: errorMessage
    })
  }

  const { displayErrors, responseError, isValid, isConfirmationModalOpen, closeConfirmationModal, onSubmit } =
    useNewsletterSubscription({
      subscribeSource: props.blok.subscribeSource,
      createIfNotExist: false,
      onSuccess: onSuccessfulSubmitDataLayer,
      onError: onErrorSubmitDataLayer
    })

  const privacyPolicyText = computed(() =>
    props.blok.privacyPolicyText ? marked.parse(props.blok.privacyPolicyText) : ''
  )

  const target = ref<HTMLElement | null>(null)
  onElementVisibleAtThreshold(target, () => {
    hasSeenSubscribeBlockPopup.value = true

    pushSubscribeBlockDataLayer({
      event_category: 'impressions'
    })
  })

  const showSubscribeBlock = ref(!hasSeenSubscribeBlockPopup.value)
  const showPopup = ref(false)

  onMounted(() => {
    showPopup.value = isDesktop.value && !hasSeenSubscribeBlockPopup.value
  })
</script>

<template>
  <div
    v-if="isDesktop"
    class="fixed right-0 top-1/2 z-10 flex transform flex-row-reverse items-center"
  >
    <Transition
      enter-active-class="transition-opacity duration-300 ease-linear"
      leave-active-class="transition-opacity duration-300 ease-linear"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <button
        v-if="!showSubscribeBlock && blok.buttonTitle?.length"
        class="absolute flex min-w-64 origin-bottom-right cursor-pointer gap-4 text-nowrap rounded-t-md border bg-bt-green px-5 py-3 [transform:rotate(-90deg)_translateX(50%)]"
        @click="showSubscribeBlock = !showSubscribeBlock"
      >
        <p class="m-0 text-bt-blue-ui text-subtitle-1">{{ blok.buttonTitle }}</p>
        <Icon
          name="common/chevron-left"
          class="rotate-90"
        />
      </button>

      <div
        v-else-if="showSubscribeBlock"
        ref="target"
        class="absolute flex w-96 rounded-md bg-white shadow-modal [transform:translateX(calc((var(--scrollbar-width,0rem)+1rem)*-1))]"
      >
        <div class="flex flex-col border-r px-6 py-8">
          <h4
            v-if="props.blok.title"
            class="mb-6 text-bt-blue-dark md:!mb-4"
          >
            {{ props.blok.title }}
          </h4>

          <p
            v-if="responseError"
            class="error transform text-dark-error transition-all text-body-3"
          >
            {{ responseError }}
          </p>

          <div class="flex flex-col gap-4">
            <div class="flex w-full flex-col gap-2">
              <UiInput
                :label="$t('forms.emailAddress')"
                :placeholder="$t('forms.subscribe.email')"
                :error="displayErrors.emailAddress"
                name="emailAddress"
                path="emailAddress"
                input-key="emailAddress"
                autofocus
              />
            </div>

            <span
              class="text-bt-blue-dark text-body-1 [&_a]:text-bt-blue-ui [&_a]:underline"
              v-html="privacyPolicyText"
            />

            <UiButton
              :title="$t('forms.signUp')"
              variant="primary"
              class="w-fit"
              @click="onSubmit()"
            />
          </div>
        </div>

        <div class="flex items-center justify-center px-3">
          <button
            :aria-label="$t('subscribe.popUp.close')"
            @click="showSubscribeBlock = !showSubscribeBlock"
          >
            <Icon
              class="text-bt-blue-dark"
              name="common/close"
            />
          </button>
        </div>
      </div>
    </Transition>
  </div>

  <LazyUiModalNotification
    v-model="isConfirmationModalOpen"
    :is-valid="isValid"
  >
    <template #title>
      {{ isValid ? $t('forms.subscribe.success.title') : $t('forms.subscribe.fail.title') }}
    </template>
    <template #description>
      {{ isValid ? $t('forms.subscribe.success.message') : $t('forms.subscribe.fail.message') }}
    </template>
    <template #button>
      <UiButton
        :title="$t('continue')"
        variant="secondary"
        size="lg"
        class="mt-8 w-full md:w-fit"
        @click="closeConfirmationModal"
      />
    </template>
  </LazyUiModalNotification>
</template>
